import { useCallback, useRef } from "react";
import { makeDefaultDeps } from "../commands/deps.js";
import { reforecast as reforecastCommand } from "../commands/reforecast.js";

/**
 * React binding for the reforecast command (EPIC #157, phase P5.3). The operation moved
 * verbatim to `commands/reforecast.ts`; this hook packs the App's React handles into the
 * command deps and exposes `reforecast` memoized. No `dispatchRun`/`runStateRef` args —
 * the command reads/writes the external run-store directly (via `makeDefaultDeps().store`).
 * The dep array is the ORIGINAL reactive set, so `reforecast`'s identity changes on the
 * same renders as before (the ↻ button + any consumers re-bind unchanged).
 */
export function useReforecast(deps) {
  const {
    forecastTracking,
    forecastLiveProgressEnabled,
    active,
    loading,
    forecastEngine,
    mockToday,
    startResolverPoll,
    setLoading,
    setForecastingPhase,
    setLastObs,
    getTourFixture,
    getTourBranchIncrease,
    slideCache,
  } = deps;
  const forecastLiveProgressEnabledRef = useRef(forecastLiveProgressEnabled);
  forecastLiveProgressEnabledRef.current = forecastLiveProgressEnabled;

  // `opts.engine` runs this one update on a given forecaster rather than the selected one (Update
  // now keeps the forecast's own depth; a Sooth Labs copy's first update keeps the forecaster
  // Sayer used). `opts.origin` /
  // `opts.surface` name the control and area for its intent, when the run starts too late after
  // the press for the intent to find them itself.
  const reforecast = useCallback(
    (opts) =>
      reforecastCommand(
        {
          ...makeDefaultDeps(),
          ui: {
            forecastTracking,
            forecastLiveProgressEnabled,
            active,
            loading,
            forecastEngine:
              (opts && typeof opts.engine === "string" && opts.engine) || forecastEngine,
            mockToday,
            setLoading,
            setForecastingPhase,
            setLastObs,
          },
          callbacks: { startResolverPoll },
          refs: { slideCache, forecastLiveProgressEnabledRef },
          tour: { getTourFixture, getTourBranchIncrease },
        },
        { origin: opts?.origin, surface: opts?.surface },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      forecastTracking,
      forecastLiveProgressEnabled,
      active,
      loading,
      forecastEngine,
      mockToday,
      startResolverPoll,
      slideCache,
    ],
  );

  return { reforecast };
}
